import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X, Check } from "lucide-react";
import { PERSONAS, registerPath } from "./personas";

const ease = [0.25, 0.1, 0.25, 1];

/** "Qui êtes-vous ?" — 7 large editorial persona cards + reveal modal. */
export default function PersonaSection() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [active]);

  return (
    <section id="personas" className="relative bg-[#0F0E0D] text-[#F7F5F0] py-24 sm:py-32" data-testid="section-personas">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-8 items-end mb-14">
          <div className="lg:col-span-7">
            <p className="caption-label text-[#C68E59] tracking-[0.25em]">Chapitre 01</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] mt-4">
              Qui <span className="italic text-[#E6C29A]">êtes-vous</span> ?
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#B5AEA5] text-sm sm:text-base font-light leading-relaxed lg:pb-2">
            Sept métiers, une même scène. Choisissez le vôtre et découvrez en un instant ce qu'Helencia change pour vous.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="persona-grid">
          {PERSONAS.map((p, i) => (
            <PersonaCard key={p.id} p={p} index={i} onOpen={() => setActive(p)} big={i === 0} />
          ))}
        </div>
      </div>

      <AnimatePresence>{active && <PersonaModal p={active} onClose={() => setActive(null)} />}</AnimatePresence>
    </section>
  );
}

function PersonaCard({ p, index, onOpen, big }) {
  return (
    <motion.button
      type="button"
      onClick={onOpen}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease }}
      className={`group relative text-left overflow-hidden border border-white/10 hover:border-[#C68E59]/60 bg-[#181614] transition-colors duration-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C68E59] ${
        big ? "col-span-2 aspect-[4/3] md:aspect-[3/2] lg:aspect-auto lg:row-span-1" : "aspect-[3/4]"
      }`}
      data-testid={`persona-card-${p.id}`}
    >
      <img
        src={p.image}
        alt={p.title}
        loading={index < 3 ? "eager" : "lazy"}
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E0D] via-[#0F0E0D]/35 to-transparent" />
      <div className="absolute inset-0 bg-[#C68E59]/0 group-hover:bg-[#C68E59]/10 transition-colors duration-500" />
      <span className="absolute top-4 left-4 font-serif text-sm text-[#E6C29A]/90 tracking-[0.2em]">{p.num}</span>
      <span className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/25 bg-black/20 backdrop-blur-md flex items-center justify-center text-white opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
        <ArrowUpRight className="w-4 h-4" />
      </span>
      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-6">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#E6C29A] mb-1.5">{p.subtitle}</p>
        <h3 className={`font-serif leading-[1.05] tracking-tight ${big ? "text-3xl sm:text-4xl lg:text-5xl" : "text-2xl sm:text-3xl"}`}>{p.title}</h3>
        <p className={`text-[#D8D2C8] text-xs sm:text-sm font-light leading-relaxed mt-2 ${big ? "max-w-md" : "hidden sm:block line-clamp-2"}`}>{p.hook}</p>
      </div>
    </motion.button>
  );
}

function PersonaModal({ p, onClose }) {
  const Icon = p.icon;
  return (
    <motion.div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-6"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      data-testid="persona-modal"
    >
      <div className="absolute inset-0 bg-[#0F0E0D]/80 backdrop-blur-md" onClick={onClose} data-testid="persona-modal-overlay" />
      <motion.div
        role="dialog" aria-modal="true" aria-label={p.title}
        initial={{ opacity: 0, y: 40, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ type: "spring", stiffness: 260, damping: 28 }}
        className="relative w-full sm:max-w-5xl max-h-[92vh] overflow-y-auto bg-[#181614] text-[#F7F5F0] border border-white/10 shadow-[0_40px_120px_-40px_rgba(0,0,0,0.9)] grid md:grid-cols-5 rounded-t-3xl sm:rounded-none"
      >
        <div className="md:col-span-2 relative aspect-[4/3] md:aspect-auto md:min-h-[560px]">
          <img src={p.image} alt={p.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#181614] via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#181614]" />
          <span className="absolute top-5 left-5 font-serif text-sm text-[#E6C29A] tracking-[0.2em]">{p.num}</span>
        </div>
        <div className="md:col-span-3 p-7 sm:p-10 lg:p-12 relative">
          <button onClick={onClose} className="absolute top-5 right-5 w-10 h-10 rounded-full border border-white/15 hover:border-[#C68E59] hover:text-[#E6C29A] flex items-center justify-center transition-colors" aria-label="Fermer" data-testid="persona-modal-close-button">
            <X className="w-4 h-4" />
          </button>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#C68E59]">
            <Icon className="w-3.5 h-3.5" /> {p.subtitle}
          </div>
          <h3 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-[1.02] mt-4" data-testid="persona-modal-title">{p.title}</h3>
          <p className="text-[#D8D2C8] text-base sm:text-lg font-light leading-relaxed mt-5 max-w-xl">{p.hook}</p>

          <ul className="mt-8 space-y-3.5">
            {p.benefits.map((b) => (
              <li key={b} className="flex items-start gap-3 text-sm text-[#E8E2D8]">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-[#C68E59]/15 border border-[#C68E59]/40 flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-[#E6C29A]" /></span>
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>

          <p className="font-serif italic text-[#B5AEA5] text-lg mt-8 border-l border-[#C68E59]/50 pl-4">{p.quote}</p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Link to={registerPath(p)} className="inline-flex items-center gap-2 bg-[#C68E59] hover:bg-[#A26F3E] text-[#0F0E0D] hover:text-white text-sm font-semibold tracking-wide px-7 h-12 transition-colors" data-testid="persona-modal-cta-button">
              {p.cta} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/login" className="text-sm text-[#B5AEA5] hover:text-[#E6C29A] transition-colors underline-offset-4 hover:underline" data-testid="persona-modal-login-link">J'ai déjà un compte</Link>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
